import { DEPARTMENT_QUERY_KEY, mutateDepartment } from "@/api/department/useDepartmentQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import customAlert from "@/features/ui/alert/alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Box, Button, Checkbox, Flex, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  DepartmentsGetRequest,
  MasterApiDepartmentsPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Checkbox,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
});

interface DepartmentCreateFormProps {
  code: string;
}

export const DepartmentCreateForm = ({ code }: DepartmentCreateFormProps) => {
  const { closeModal } = useModal();
  const { t } = useTranslation();

  const form = useForm<DepartmentsGetRequest>({
    initialValues: {
      code: code,
      name: "",
    },
    validate: {
      code: (value) => (value ? null : t("placeholder.input", { item: `${t("department.code")}` })),
      name: (value) => (value ? null : t("placeholder.input", { item: `${t("department.name")}` })),
    },
  });
  const queryClient = useQueryClient();
  const { mutate: createMutate } = useMutation(
    (params: MasterApiDepartmentsPostRequest) =>
      mutateDepartment.create(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([DEPARTMENT_QUERY_KEY]);
        customAlert(
          `${t("message.createSuccess", { item: `${t("department.department")}` })}`,
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error: AxiosError<Error>) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("department.code") }),
          `${t("common.createFail", { item: `${t("department.department")}` })}`,
          "red",
        );
      },
    },
  );

  const onSubmit = (values: DepartmentsGetRequest) => {
    createMutate({
      departmentsGetRequest: values,
    });
    closeModal({});
  };

  return (
    <Box>
      <form
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          width: "30rem",
          height: "15rem",
        }}
        onSubmit={form.onSubmit((values) => onSubmit && onSubmit(values))}
      >
        <Create.Input
          withAsterisk
          label={t("department.code")}
          placeholder={t("placeholder.input", { item: `${t("department.code")}` })}
          {...form.getInputProps("code")}
        />
        <Create.Input
          withAsterisk
          label={t("department.name")}
          placeholder={t("placeholder.input", { item: `${t("department.name")}` })}
          {...form.getInputProps("name")}
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Create.Button>
          <Create.Button disabled={!form.isValid()} type="submit">
            {t("common.create", { item: `${t("department.department")}` })}
          </Create.Button>
        </Create.ButtonBox>
      </form>
    </Box>
  );
};
